import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, LoaderCircle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { changeContractOptions, itemsOutsideCostForecast } from '../lib/changes'
import type { CostChange, CostContract, CostLineView, CostRow } from '../types'

const NONE = 'none'

interface Props {
  open: boolean
  change: CostChange
  contracts: CostContract[]
  /** The contracts query has not answered yet, or failed: an empty list does not mean "no active contract" then. */
  contractsState: 'loading' | 'error' | 'ready'
  lines: CostLineView[]
  /** The budget grid rows, for the warning about items forecast from their budget; empty while not loaded. */
  rows: CostRow[]
  onOpenChange: (open: boolean) => void
  /** Resolves true when the server accepted the conversion; the dialog stays open on failure. */
  onConvert: (contractId: string) => Promise<boolean>
}

/** Potential changes spec §8: "Převést na změnu smlouvy" picks an ACTIVE subcontract, pre-filled with the linked one. */
const ConvertContractDialog: React.FC<Props> = ({ open, change, contracts, contractsState, lines, rows, onOpenChange, onConvert }) => {
  const { t } = useTranslation()
  const options = changeContractOptions(contracts, 'CONTRACT_CHANGE')
  // undefined until the user picks: the linked contract stays pre-selected even when the contracts arrive after opening.
  const [picked, setPicked] = useState<string | null | undefined>(undefined)
  const [submitting, setSubmitting] = useState(false)
  const contractId = picked === undefined ? (options.some((c) => c.id === change.contractId) ? change.contractId : null) : picked
  const outside = itemsOutsideCostForecast(lines, rows)

  useEffect(() => {
    if (!open) return
    setPicked(undefined)
    setSubmitting(false)
  }, [open])

  const submit = async () => {
    if (!contractId || submitting) return
    setSubmitting(true)
    const ok = await onConvert(contractId)
    setSubmitting(false)
    if (ok) onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('CostControl_potential_convert_contract')}</DialogTitle>
          <DialogDescription>{t('CostControl_potential_convert_contract_hint')}</DialogDescription>
        </DialogHeader>
        {contractsState === 'loading' ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground"><LoaderCircle className="h-4 w-4 animate-spin" />{t('Common_loading')}</p>
        ) : contractsState === 'error' ? (
          <p className="text-sm text-destructive">{t('CostControl_potential_contracts_failed')}</p>
        ) : options.length ? (
          <div className="space-y-1.5">
            <Label htmlFor="cc-convert-contract">{t('CostControl_change_field_contract')} *</Label>
            <Select value={contractId ?? NONE} onValueChange={(v) => setPicked(v === NONE ? null : v)}>
              <SelectTrigger id="cc-convert-contract"><SelectValue /></SelectTrigger>
              <SelectContent>
                {!contractId && <SelectItem value={NONE}>{t('CostControl_change_pick_contract')}</SelectItem>}
                {options.map((c) => <SelectItem key={c.id} value={c.id}>{c.number ? `${c.number} · ${c.name}` : c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('CostControl_potential_no_active_contract')}</p>
        )}
        {outside.length > 0 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{t('CostControl_potential_convert_outside_forecast', { items: outside.join(', ') })}</AlertDescription>
          </Alert>
        )}
        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>{t('Common_cancel')}</Button>
          <Button onClick={submit} disabled={submitting || !contractId}>
            {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('CostControl_potential_convert')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default ConvertContractDialog
